/**
 * Member profile page component.
 * Displays public profile information for a member by username.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trpc } from '../utils/trpc';
import styles from '../styles/MemberProfile.module.css';

type BadgeEntry = {
  level: number;
  awarded_at: string;
  badges:
    | {
        id: string;
        name: string;
        description: string | null;
        icon_url: string | null;
      }
    | {
        id: string;
        name: string;
        description: string | null;
        icon_url: string | null;
      }[]
    | null;
};

function MemberProfile() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [reported, setReported] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showBadges, setShowBadges] = useState(true);

  const {
    data: profile,
    isLoading: profileLoading,
    error,
  } = trpc.member.getByUsername.useQuery(
    { username: username || '' },
    { enabled: !!username },
  );

  const { data: userTags, isLoading: tagsLoading } = trpc.tags.getUserTags.useQuery(
    { userId: profile?.id || '' },
    { enabled: !!profile?.id },
  );

  const { data: userBadges, isLoading: badgesLoading } =
    trpc.badge.getUserBadges.useQuery(
      { userId: profile?.id || '' },
      { enabled: !!profile?.id },
    );

  const handleClose = () => navigate(user ? '/dashboard' : '/');

  const handleReport = () => {
    setReported(true);
    setShowMenu(false);
  };

  const isLoading = profileLoading || tagsLoading || badgesLoading;

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.profileCard}>
          <div className={styles.heroBanner} />
          <button className={styles.closeButton} onClick={handleClose} aria-label="Close">
            ✕
          </button>
          <div className={styles.cardInner}>
            <div className={styles.skeletonAvatar} />
            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonText} />
            <div className={styles.skeletonText} />
            <div className={styles.skeletonTags}>
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className={styles.container}>
        <div className={styles.profileCard}>
          <div className={styles.heroBanner} />
          <button className={styles.closeButton} onClick={handleClose} aria-label="Close">
            ✕
          </button>
          <div className={styles.cardInner}>
            <div className={styles.errorContent}>
              <div className={styles.errorIcon}>🙁</div>
              <h2 className={styles.errorTitle}>Profile Not Found</h2>
              <p className={styles.errorMessage}>
                {error ? error.message : `The user @${username} doesn't exist.`}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tags = userTags?.map((ut: any) => ut.tags.name) || [];
  const badges =
    userBadges
      ?.map((entry: BadgeEntry) => {
        const badgeData = Array.isArray(entry.badges) ? entry.badges[0] : entry.badges;
        if (!badgeData) return null;
        return {
          id: badgeData.id,
          name: badgeData.name,
          description: badgeData.description,
          iconUrl: badgeData.icon_url,
          level: entry.level,
        };
      })
      .filter((badge): badge is NonNullable<typeof badge> => !!badge) || [];
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');

  return (
    <div className={styles.container}>
      <div className={styles.profileCard}>
        {/* Hero Banner */}
        <div className={styles.heroBanner} />

        {/* Controls */}
        <div className={styles.menuContainer}>
          <button
            className={styles.menuButton}
            onClick={() => setShowMenu(!showMenu)}
            aria-label="Options menu"
          >
            ⋮
          </button>
          {showMenu && (
            <div className={styles.dropdown}>
              <button
                className={styles.dropdownItem}
                onClick={handleReport}
                disabled={reported}
              >
                {reported ? '✓ Reported' : '🚩 Report Member'}
              </button>
            </div>
          )}
        </div>

        <button className={styles.closeButton} onClick={handleClose} aria-label="Close">
          ✕
        </button>

        {/* Card Body */}
        <div className={styles.cardInner}>
          {/* Avatar */}
          {profile.profile_picture_url ? (
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={profile.profile_picture_url}
              alt={`${profile.username}'s profile`}
              className={styles.profilePicture}
            />
          ) : (
            <div className={styles.defaultAvatar}>
              {profile.username.charAt(0).toUpperCase()}
            </div>
          )}

          {/* Username */}
          <h1 className={styles.username}>@{profile.username}</h1>

          {/* Name + Pronouns inline row */}
          {(fullName || profile.pronouns) && (
            <div className={styles.subLine}>
              {fullName && <span className={styles.subName}>{fullName}</span>}
              {fullName && profile.pronouns && <span className={styles.subDot}>•</span>}
              {profile.pronouns && (
                <span className={styles.subPronouns}>{profile.pronouns}</span>
              )}
            </div>
          )}

          {/* Description */}
          {profile.description && (
            <div className={styles.description}>{profile.description}</div>
          )}

          {/* Badges */}
          <div className={styles.badgesSection}>
            <div className={styles.badgesHeader}>
              <h2 className={styles.badgesTitle}>Badges</h2>
              <button
                className={styles.badgesToggle}
                onClick={() => setShowBadges(!showBadges)}
                aria-expanded={showBadges}
              >
                {showBadges ? 'Hide' : 'View'}
                <span
                  className={`${styles.badgesChevron} ${showBadges ? styles.badgesChevronOpen : ''}`}
                >
                  ▼
                </span>
              </button>
            </div>

            <div
              className={`${styles.badgesBody} ${showBadges ? styles.badgesBodyOpen : ''}`}
            >
              {profile.is_private ? (
                <div className={styles.emptyBadges}>
                  <p className={styles.emptyBadgesText}>This profile is private</p>
                </div>
              ) : badges.length > 0 ? (
                <div className={styles.badgesGrid}>
                  {badges.map((badge) => (
                    <div key={badge.id} className={styles.badgeCard}>
                      {badge.iconUrl ? (
                        <img
                          onLoad={(e) => e.currentTarget.classList.add('loaded')}
                          src={badge.iconUrl}
                          alt={badge.name}
                          className={styles.badgeIcon}
                        />
                      ) : (
                        <div className={styles.badgeIconFallback}>🏅</div>
                      )}
                      <div className={styles.badgeMeta}>
                        <p className={styles.badgeName}>{badge.name}</p>
                        <p className={styles.badgeLevel}>Level {badge.level}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyBadges}>
                  <p className={styles.emptyBadgesText}>No badges earned yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className={styles.tagsSection}>
            <div className={styles.tagsHeader}>
              <h2 className={styles.tagsTitle}>Interests</h2>
              <button
                className={styles.tagsToggle}
                onClick={() => setShowTags(!showTags)}
                aria-expanded={showTags}
              >
                {showTags ? 'Hide' : 'View'}
                <span
                  className={`${styles.tagsChevron} ${showTags ? styles.tagsChevronOpen : ''}`}
                >
                  ▼
                </span>
              </button>
            </div>
            <div className={`${styles.tagsBody} ${showTags ? styles.tagsBodyOpen : ''}`}>
              {profile.is_private ? (
                <div className={styles.emptyTags}>
                  <p aria-label="emptyTagsText" className={styles.emptyTagsText}>
                    This profile is private
                  </p>
                </div>
              ) : tags.length > 0 ? (
                <div className={styles.tagsContainer}>
                  {tags.map((tag: string, index: number) => (
                    <span key={index} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyTags}>
                  <p className={styles.emptyTagsText}>No interests yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MemberProfile;
